import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useTrackPageView } from "@/lib/tracker";
import PausedSite from "@/components/PausedSite";

import PublicEcommerceShell from "@/pages/PublicEcommerceShell";
import PublicServiceShell from "@/pages/PublicServiceShell";
import PublicRestaurantShell from "@/pages/PublicRestaurantShell";
import TemplateHome from "@/pages/template-ecommerce/Home";
import { ECOM_STATIC_PAGES } from "@/pages/template-ecommerce/StaticPages";

/**
 * Resolves `/site/:slug` and `/site/:slug/:pageSlug`.
 *
 * Every renderable site uses one of the three SRC template families:
 *  - ecommerce  → PublicEcommerceShell (Home, static sub-pages, shop chrome).
 *                 Unknown/custom slugs fall back to the shell's Home layout.
 *  - service    → PublicServiceShell (handles all sub-page slugs internally).
 *  - restaurant → PublicRestaurantShell (single long-scroll page).
 * Anything else 404s server-side and renders the not-found card.
 */
export default function PublicSite() {
  const { slug, pageSlug } = useParams();
  useTrackPageView(slug, pageSlug ? `/${pageSlug}` : "/");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null); // 'not_found' | 'server'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = pageSlug && pageSlug !== "home"
          ? `/public/site/${slug}/page/${pageSlug}`
          : `/public/site/${slug}`;
        const { data } = await api.get(url);
        if (cancelled) return;
        setData(data);
        const seoTitle = data.page?.seo_title || data.website_content?.seo_title || data.summary?.name || "Business";
        document.title = seoTitle;
        const meta = document.querySelector('meta[name="description"]') || (() => {
          const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
        })();
        meta.content = data.page?.meta_description || data.website_content?.meta_description || "";
      } catch (e) {
        const status = e?.response?.status;
        if (!cancelled) setErrorState(status === 404 ? "not_found" : "server");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug, pageSlug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white">Loading…</div>;
  if (errorState === "server") return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]" data-testid="public-server-error">
      <div className="nb-card p-8 text-center max-w-md">
        <div className="font-display text-2xl font-extrabold mb-2">Something went wrong</div>
        <p className="text-gray-600 text-sm">We couldn&apos;t load this site right now. Please try again in a moment.</p>
        <button onClick={() => window.location.reload()} className="inline-block mt-4 text-sm font-semibold underline">Retry</button>
      </div>
    </div>
  );
  if (errorState === "not_found") return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]" data-testid="public-not-found">
      <div className="nb-card p-8 text-center max-w-md">
        <div className="font-display text-2xl font-extrabold mb-2">{pageSlug ? "Page not found" : "Site not found"}</div>
        <p className="text-gray-600 text-sm">{pageSlug ? "This page may have been moved or removed." : "This website may have been moved or unpublished."}</p>
        {pageSlug && <a href={`/site/${slug}`} className="inline-block mt-4 text-sm font-semibold underline">Back to home</a>}
      </div>
    </div>
  );

  // Hosting-paused site (Website #2+ without active Additional Website
  // Hosting) → branded platform page, never the raw 404 appearance.
  if (data?.paused) return <PausedSite />;

  if (data.industry_template === "ecommerce") {
    // Standard ecommerce sub-pages render as real SRC pages inside the
    // shell; the bare slug and any custom slug render the shell's Home.
    const Page = (pageSlug && ECOM_STATIC_PAGES[pageSlug]) || TemplateHome;
    return <PublicEcommerceShell Page={Page} />;
  }

  if (data.industry_template === "service" && data.service_config) {
    return (
      <PublicServiceShell
        slug={slug}
        pageSlug={pageSlug || ""}
        serviceConfig={data.service_config}
      />
    );
  }

  if (data.industry_template === "restaurant" && data.restaurant_config) {
    return (
      <PublicRestaurantShell
        slug={slug}
        restaurantConfig={data.restaurant_config}
      />
    );
  }

  // Unrenderable payload (shouldn't happen — backend 404s non-SRC sites).
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFDF0]" data-testid="public-not-found">
      <div className="nb-card p-8 text-center max-w-md">
        <div className="font-display text-2xl font-extrabold mb-2">Site not found</div>
        <p className="text-gray-600 text-sm">This website may have been moved or unpublished.</p>
      </div>
    </div>
  );
}
